// import { UniversalSentryOptionsCommon } from '@lib/sentry/universal-config';

const getSentryTimestamp = () =>
  `${new Date().toISOString().split('T')[0]}-${new Date()
    .toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    .replace(':', '')}`;

/**
 * Used to group events to environments within Sentry and should reflect
 * deployment environments (e.g. production, ..., test, ci-test) or
 * code stability stages (e.g. production, staging, development)
 *
 * WARNING: should not(!) fallback to "production" to avoid data contamination
 */
export const getSentryEnvironment = () =>
  process.env.SENTRY_ENVIRONMENT ??
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ??
  'development';

/**
 * The release dist is intended to distinct build versions of a named release.
 *
 * SENTRY_DIST should be set in CI to e.g. the git SHA or
 * a projects semver or some other unique identifier.
 */
export const getSentryReleaseDist = () =>
  process.env.SENTRY_DIST ??
  process.env.NEXT_PUBLIC_SENTRY_DIST ??
  // Fallback to a default release dist in the format of YYYY-MM-DD-HHMM
  getSentryTimestamp();

export const dsn =
  'https://e540b3ee787cd0aeaaa60dd51aced7a0@o4507418626621440.ingest.de.sentry.io/4507418630946896';

/**
 * The release name should be set by CI and include a unique project identifier.
 *
 * Scheme <app>@<git-sha|semver|YYYY-MM-DD-HHMM>
 *
 * Should reflect a reference to a version of code that may be kept across environments to
 * inform about the release health for that version of code.
 *
 * > Note that releases are global per organization.
 * > If you want the releases in different projects to be treated as separate entities,
 * > make the version name unique across the organization.
 * see https://docs.sentry.io/cli/releases/
 */
export const getSentryReleaseName = () =>
  process.env.SENTRY_RELEASE ??
  process.env.NEXT_PUBLIC_SENTRY_RELEASE ??
  // Fallback to a default release name in the format of my-trips@YYYY-MM-DD-HHMM
  `my-trips@${getSentryTimestamp()}`;

/**
 *  Sentry monitoring has to be enabled for production environments, unless
 *  there is a specific need to force deactivation, hence it will be enabled
 *  by default.
 *
 *  For all staging environments, like *.aws.test.meinschiff.com, it has to be
 *  enabled as well, if not temporarily required otherwise, hence it will be
 *  enabled via environment configuration.
 *
 *  For pre-QA preview and development environments, like e.g. local machine or
 *  branch-previews, it depends and therefor is opt-in and disabled by default.
 */
export const isEnabled = () => {
  const disabledByEnv =
    // override for environments that are enabled by default - e.g. production
    process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'false' ||
    process.env.NEXT_PUBLIC_SENTRY_ENABLED === '0';

  if (disabledByEnv) {
    return false;
  }

  const enabledByEnv =
    // set this environment var in staging environments
    process.env.NEXT_PUBLIC_SENTRY_ENABLED === 'true' ||
    process.env.NEXT_PUBLIC_SENTRY_ENABLED === '1';

  if (enabledByEnv) {
    return true;
  }

  const sentryEnvironment = getSentryEnvironment();
  const enabledBySentryEnvironment = sentryEnvironment === 'production';

  return enabledByEnv || enabledBySentryEnvironment;
};

/**
 * Without a special reason a Sentry release should only be created during CI/CD
 * when a build is made and destined to be deployed.
 *
 * If it is not destined for deployment yet, e.g. a PR build without deployment,
 * there is no need for a release. Also usually not during local development,
 * unless there is a need for that.
 */
export const isReleaseCreateEnabled = () => {
  // If Sentry should be enabled, we should create a release, as on an enabled
  // Sentry application it will get created with the first events anyways
  return isEnabled();
};

/**
 * Determines if debug mode should be enabled for the sentry config.
 *
 * @returns {boolean}
 */
export const isSentryDebugEnabled = () =>
  process.env.SENTRY_DEBUG === 'true' ||
  process.env.NEXT_PUBLIC_SENTRY_DEBUG === 'true' ||
  process.env.SENTRY_DEBUG === '1' ||
  process.env.NEXT_PUBLIC_SENTRY_DEBUG === '1';

// TODO JOK: ensure dsn, environment and release are set
export const getSharedSentryConfig = () => {
  const enabled = isEnabled();
  const environment = getSentryEnvironment();
  const release = getSentryReleaseName();
  const dist = getSentryReleaseDist();
  const debug = isSentryDebugEnabled();

  return {
    // Setting this to enabled: false doesn't prevent all overhead from Sentry instrumentation.
    // To disable Sentry completely, depending on environment, call `Sentry.init conditionally.
    enabled, // TODO JOK: maybe we should prefer disabling it completely?
    dsn,
    environment,
    release,
    dist,
    // Base sample rate for errors - distinct from tracesSampleRate or tracesSampler
    sampleRate: 1,

    // Setting this option to true will print useful information to the console while you're setting up Sentry.
    debug,
  };
};
